import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Gift, Sparkles, ArrowRight, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Dismiss is per-browser-session (sessionStorage): if the user closes
// the popup they won't see it again on the same tab, but a new visit /
// new tab brings it back. The popup keeps firing until the user
// actually completes a purchase (PURCHASED_KEY in localStorage).
const DISMISS_SESSION_KEY = 'iiy_bumper_dismissed_session';
const PURCHASED_KEY = 'iiy_has_purchased';
const SHOW_AFTER_MS = 1000;
const FIRST_TIME_PRICE = 37.99;

function hasPurchased() {
  if (typeof window === 'undefined') return false;
  try { return !!localStorage.getItem(PURCHASED_KEY); } catch (e) { return false; }
}

function dismissedThisSession() {
  if (typeof window === 'undefined') return false;
  try { return !!sessionStorage.getItem(DISMISS_SESSION_KEY); } catch (e) { return false; }
}

function shouldShow() {
  if (hasPurchased()) return false;
  if (dismissedThisSession()) return false;
  return true;
}

function formatRemaining(ms) {
  if (ms <= 0) return null;
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms / 3600000) % 24);
  const mins = Math.floor((ms / 60000) % 60);
  const secs = Math.floor((ms / 1000) % 60);
  return { days, hours, mins, secs };
}

export default function BumperOfferPopup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Don't show on the offer page itself or on checkout pages.
  const suppressed = ['/bumper-offer', '/checkout', '/payment-success'].some(p =>
    location.pathname.startsWith(p),
  );

  // `?bumper=force` query param bypasses localStorage gating so you can
  // verify the popup on a browser that previously dismissed it.
  const forceShow = typeof window !== 'undefined' &&
    new URLSearchParams(location.search).get('bumper') === 'force';

  useEffect(() => {
    if (suppressed) return;
    // One-time migration: clear legacy localStorage keys from the
    // previous "single dismiss = permanent suppress" implementation so
    // existing visitors aren't locked out of the new behaviour.
    try {
      localStorage.removeItem('iiy_visitor_seen');
      localStorage.removeItem('iiy_bumper_dismissed_at');
    } catch (e) { /* private mode */ }
    axios.get(`${API}/bumper-offer/tiers`).then(r => {
      if (r.data?.enabled) setConfig(r.data);
    }).catch(() => {});
  }, [suppressed]);

  useEffect(() => {
    if (!config || suppressed) return;
    if (!forceShow && !shouldShow()) return;
    const t = setTimeout(() => setOpen(true), forceShow ? 300 : SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, [config, suppressed, forceShow]);

  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  // Confetti on first open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { default: confetti } = await import('canvas-confetti');
        if (cancelled) return;
        const colors = ['#16a34a', '#2d5016', '#C5B358', '#fbbf24', '#ffffff'];
        confetti({ particleCount: 80, spread: 70, origin: { y: 0.3 }, colors });
        setTimeout(() => confetti({ particleCount: 60, spread: 100, origin: { y: 0.4 }, colors }), 250);
      } catch (e) { /* confetti is optional */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const dismiss = useCallback(() => {
    // Per-tab dismissal only: closing the popup hides it for the rest
    // of this browsing session, but it'll reappear on a new visit so we
    // keep nudging the customer until they actually purchase.
    try { sessionStorage.setItem(DISMISS_SESSION_KEY, '1'); } catch (e) { /* private mode */ }
    setOpen(false);
  }, []);

  const onShopNow = () => {
    try { sessionStorage.setItem(DISMISS_SESSION_KEY, '1'); } catch (e) { /* private mode */ }
    setOpen(false);
    // Send the first-time buyer straight to qty=1 on the bumper page.
    navigate('/bumper-offer?qty=1&firsttime=1');
  };

  if (!config) return null;
  const countdown = formatRemaining(new Date(config.deadline).getTime() - now);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="bumper-popup-overlay"
          onClick={dismiss}
        >
          {/* Dimmed backdrop */}
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />

          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 30 }}
            transition={{ type: 'spring', damping: 22 }}
            className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden grid md:grid-cols-5"
            onClick={e => e.stopPropagation()}
            data-testid="bumper-popup"
          >
            {/* Close */}
            <button
              onClick={dismiss}
              aria-label="Close offer"
              data-testid="bumper-popup-close"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 hover:bg-white text-slate-700 hover:text-slate-900 flex items-center justify-center shadow-md transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Left: Bella image (2/5) */}
            <div className="relative bg-gradient-to-br from-emerald-50 to-amber-50 hidden md:block md:col-span-2">
              <img decoding="async"
                src="/bella-glanville/bella-bottle.png"
                alt="Bella Glanville with BCM-95®"
                className="w-full h-full object-cover object-center"
                loading="eager"
              />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-700/90 text-white text-[10px] font-bold uppercase tracking-wider shadow-md">
                <Sparkles className="w-3 h-3" /> Bella x IIY
              </div>
            </div>

            {/* Right: Offer content (3/5) */}
            <div className="p-6 md:p-8 md:col-span-3 bg-gradient-to-br from-white via-amber-50/20 to-emerald-50/30">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-600 text-white text-[10px] font-bold uppercase tracking-wider mb-3">
                <Gift className="w-3 h-3" /> First-Time Buyer Exclusive
              </div>
              <h2 className="font-heading text-2xl md:text-[28px] font-bold text-emerald-900 leading-tight mb-1">
                🎊 Exclusive First-Time<br />Buyer Bumper Offer
              </h2>
              <p className="text-sm text-muted-foreground mb-5">
                Try <strong className="text-emerald-800">BCM-95®</strong> today. Delivered straight to your doorstep with free UK Royal Mail 48.
              </p>

              {/* Single big price highlight */}
              <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 text-white rounded-2xl p-5 mb-4 shadow-lg text-center">
                <p className="text-xs uppercase tracking-wider opacity-80 mb-1">Your one-time welcome price</p>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-6xl font-black tabular-nums">£{FIRST_TIME_PRICE.toFixed(2)}</span>
                  <span className="text-sm opacity-90">/bottle</span>
                </div>
                <p className="text-[11px] opacity-85 mt-2">
                  <Check className="w-3 h-3 inline -mt-0.5" /> All-inclusive: Price includes VAT &amp; FREE Delivery.
                  <span className="block opacity-75 mt-0.5">No hidden fees at checkout.</span>
                </p>
              </div>

              {/* Countdown */}
              {countdown && (
                <div className="flex flex-col items-center justify-center gap-1 mb-4 bg-red-50 border border-red-200 rounded-lg py-2.5" data-testid="bumper-popup-countdown">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-red-700" />
                    <span className="text-sm font-bold text-red-700 tracking-wide tabular-nums">
                      Ends in {countdown.days}d {String(countdown.hours).padStart(2,'0')}:{String(countdown.mins).padStart(2,'0')}:{String(countdown.secs).padStart(2,'0')}
                    </span>
                  </div>
                  <span className="text-[11px] uppercase tracking-wider text-red-600/80 font-semibold">
                    Offer ends 30 June 2026
                  </span>
                </div>
              )}

              {/* CTA */}
              <button
                onClick={onShopNow}
                data-testid="bumper-popup-cta"
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-3.5 rounded-xl text-base flex items-center justify-center gap-2 shadow-lg hover:gap-3 transition-all"
              >
                CLAIM MY BUMPER OFFER NOW
                <ArrowRight className="w-4 h-4" />
              </button>

              {/* Dismiss link */}
              <button
                onClick={dismiss}
                data-testid="bumper-popup-dismiss-text"
                className="w-full mt-2 text-xs text-slate-500 hover:text-slate-700 hover:underline transition-colors text-center py-1"
              >
                No thanks, I&rsquo;ll pay full price later
              </button>

              <p className="text-[11px] text-center text-emerald-700 mt-3 font-medium">
                Earn while you invest in yourself &middot; cashback rewards coming soon
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
